import {
  Anchor,
  Box,
  Button,
  Checkbox,
  Grid,
  PasswordInput,
  TextInput
} from '@mantine/core'
import { isEmail, isNotEmpty, useForm } from '@mantine/form'
import { FormattedMessage, useIntl } from 'react-intl'
import { PasswordRules } from '@/components/ui-shared/PasswordRules/PasswordRules'
import { VP_LEGAL_TERMS_URL } from '@/config/main'
import { isStrongPassword } from '@/utils/validation/password'

const messages = {
  email: {
    id: 'email'
  },
  firstName: {
    id: 'firstName'
  },
  lastName: {
    id: 'lastName'
  },
  password: {
    id: 'password'
  },
  confirmPassword: {
    id: 'confirmPassword'
  },
  invalidEmail: {
    id: 'validation.invalidEmail'
  },
  enterYourFirstName: {
    id: 'validation.enterYourFirstName'
  },
  enterYourLastName: {
    id: 'validation.enterYourLastName'
  },
  invalidPassword: {
    id: 'validation.strongPassword.invalid'
  },
  passwordsDoNotMatch: {
    id: 'validation.passwordsDoNotMatch'
  },
  acceptLegalTerms: {
    id: 'validation.acceptLegalTerms'
  }
}

type FormValues = {
  email: string
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
  acceptLegalTerms: boolean
}

export type SubmitValues = Omit<
  FormValues,
  'confirmPassword' | 'acceptLegalTerms'
>

type SignUpFormProps = {
  initialEmail: string
  onSubmit: (formValues: SubmitValues) => void
}

export const SignUpForm = ({ initialEmail, onSubmit }: SignUpFormProps) => {
  const intl = useIntl()

  const form = useForm<FormValues>({
    initialValues: {
      email: initialEmail,
      firstName: '',
      lastName: '',
      password: '',
      confirmPassword: '',
      acceptLegalTerms: false
    },

    validate: {
      email: isEmail(intl.formatMessage(messages.invalidEmail)),
      firstName: isNotEmpty(intl.formatMessage(messages.enterYourFirstName)),
      lastName: isNotEmpty(intl.formatMessage(messages.enterYourLastName)),
      password: isStrongPassword(intl.formatMessage(messages.invalidPassword)),
      confirmPassword: (value, values) =>
        value !== values.password
          ? intl.formatMessage(messages.passwordsDoNotMatch)
          : null,
      acceptLegalTerms: isNotEmpty(
        intl.formatMessage(messages.acceptLegalTerms)
      )
    }
  })

  const handleSubmit = (formValues: FormValues) => {
    const { confirmPassword, acceptLegalTerms, ...submitValues } = formValues
    onSubmit(submitValues)
  }

  return (
    <form noValidate onSubmit={form.onSubmit((values) => handleSubmit(values))}>
      <TextInput
        type="email"
        label={intl.formatMessage(messages.email)}
        mb="lg"
        disabled
        {...form.getInputProps('email')}
      />

      <Grid mb="lg">
        <Grid.Col span={6}>
          <TextInput
            label={intl.formatMessage(messages.firstName)}
            autoFocus
            {...form.getInputProps('firstName')}
          />
        </Grid.Col>

        <Grid.Col span={6}>
          <TextInput
            label={intl.formatMessage(messages.lastName)}
            {...form.getInputProps('lastName')}
          />
        </Grid.Col>
      </Grid>

      <Box mb="lg">
        <PasswordInput
          label={intl.formatMessage(messages.password)}
          mb="sm"
          {...form.getInputProps('password')}
        />

        <PasswordRules
          password={form.getInputProps('password').value as string}
        />
      </Box>

      <PasswordInput
        label={intl.formatMessage(messages.confirmPassword)}
        mb="xl"
        {...form.getInputProps('confirmPassword')}
      />

      <Checkbox
        size="xs"
        mb="xl"
        label={
          <FormattedMessage
            id="acceptLegalTerms"
            values={{
              termsUrl: (VP_LEGAL_TERMS_URL || '').replace(/^https?:\/\//, ''), // remove 'http://' or 'https://' for display
              link: (chunks) => (
                <Anchor
                  href={VP_LEGAL_TERMS_URL}
                  target="_blank"
                  underline="always"
                  inherit
                >
                  {chunks}
                </Anchor>
              )
            }}
          />
        }
        {...form.getInputProps('acceptLegalTerms', { type: 'checkbox' })}
      />

      <Button type="submit" fullWidth>
        <FormattedMessage id="createAccount" />
      </Button>
    </form>
  )
}
